import { useState } from "react";

interface Props {
  brandId: number;
  onClose: () => void;
  onUploaded: () => void;
}

export function BrandIdentityUploadModal({ brandId, onClose, onUploaded }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      for (const f of files) form.append("images", f);
      const res = await fetch(`/api/admin/brand-intelligence/${brandId}/upload-media`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? `HTTP ${res.status}`);
        return;
      }
      onUploaded();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="bg-white border border-slate-200 rounded-xl p-6 w-[480px] max-w-[90vw] shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900 mb-3">Upload Brand Ads</h3>
        <p className="text-xs text-slate-600 mb-4">Up to 12 images at once. 10MB max per file. jpg/png/webp.</p>
        <input
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => {
            const list = Array.from(e.target.files ?? []).slice(0, 12);
            setFiles(list);
          }}
          className="block w-full mb-3 text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-slate-300 file:text-xs file:font-medium file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
        />
        <p className="text-xs text-slate-500 mb-4">{files.length} file{files.length === 1 ? "" : "s"} selected</p>
        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 text-xs disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={busy || files.length === 0} className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs disabled:opacity-50 disabled:hover:bg-blue-600">
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
